// @flow
import * as React from 'react';
import classNames from 'classnames/bind';
import { useQuery } from '@apollo/client';
import { useApolloLightbox } from '../../../hooks/use-apollo-lightbox';
import AiProductsModal from '../../ai-products-modal';
import Button from '../../../basic-components/button';
import FlagIcon from '../../../basic-components/flag-icon';
import Switcher from '../../../basic-components/switcher';
import { StockData } from '../../../shared-queries/StockData.graphql';
import Thumbnail from '../../../basic-components/thumbnail';
import t from './locale';
import cs from './styles.pcss';

const cx = classNames.bind(cs);

type Product = {
  id: string,
  title?: string,
  name?: string,
  sku?: string,
  quantity?: number,
  totalQuantity?: number,
  pictures?: Array<{ thumbnailUrl?: string }>,
};

type Props = {
  products: Array<Product>,
  queryParams: { [string]: string },
};

/**
 * AI Chatbox Products Table Component
 * Simplified product table for displaying product lists in AI chat
 */
export default function AiProductsTable({
  products,
  queryParams,
}: Props): React.Node {
  const [modalOpened, setModalOpened] = React.useState(false);
  const [selectedProductId, setSelectedProductId] = React.useState(null);

  const { data: stockData } = useQuery(StockData);
  const showcases = stockData?.showcases ?? [];
  const { openLightbox, closeLightbox } = useApolloLightbox(
    'ai-products-table-modal',
  );

  // Handle row click - open modal to show a single product
  const handleRowClick = (productId: string) => {
    setSelectedProductId(productId);
    setModalOpened(true);
    openLightbox();
  };

  // Handle View Items button click - open modal to show all products
  const handleViewAll = () => {
    setSelectedProductId(null);
    setModalOpened(true);
    openLightbox();
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpened(false);
    closeLightbox();
  };

  if (!queryParams) {
    return null;
  }

  return (
    <div className={cx('ai-products-table-container')}>
      <div className={cx('ai-products-header')}>
        <div className={cx('products-count')}>{t('productsFound')}</div>
        <div className={cx('header-actions')}>
          <Button onClick={handleViewAll} size="small" mode="main">
            {t('viewAll')}
          </Button>
        </div>
      </div>

      <div className={cx('table-wrapper')}>
        <table className={cx('products-table')}>
          <thead>
            <tr>
              <th className={cx('title-col')}>Title</th>
              <th className={cx('sku-col')}>SKU</th>
              <th className={cx('quantity-col')}>Quantity</th>
              {showcases.map(({ id, site }) => (
                <th key={id} className={cx('country-col')}>
                  <FlagIcon name={site.code} size={20} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map((product) => {
              const productName = product.title || product.name || 'Product';

              // Extract SKU from variations array if exists
              const sku = product.sku || product.variations?.[0]?.sku || 'N/A';

              return (
                <tr
                  key={product.id}
                  className={cx('product-row')}
                  onClick={() => handleRowClick(product.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className={cx('title-cell')}>
                    <div className={cx('title-content')}>
                      <Thumbnail
                        src={product.pictures?.[0]?.thumbnailUrl || null}
                        alt={productName}
                        className={cx('product-thumbnail')}
                      />
                      <span className={cx('product-title')}>{productName}</span>
                    </div>
                  </td>
                  <td className={cx('sku-cell')}>{sku}</td>
                  <td className={cx('quantity-cell')}>
                    {product.totalQuantity || product.quantity || 0}
                  </td>
                  {showcases.map(({ id }) => (
                    <td key={id} className={cx('country-cell')}>
                      <Switcher disabled state="off" mode="success" />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modalOpened && (
        <AiProductsModal
          queryParams={queryParams}
          selectedProductId={selectedProductId}
          onClose={handleCloseModal}
          fallbackProducts={products}
        />
      )}
    </div>
  );
}
